package twist.mena.sports.admin.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.NavState
import twist.mena.sports.admin.state.Screen
import twist.mena.sports.shared.dto.admin.*
import twist.mena.sports.shared.dto.legacy.LegacyLeagueData
import twist.mena.sports.shared.dto.legacy.LegacyTeamData
import twist.mena.sports.shared.dto.legacy.LegacyPlayerData

class AdminApiClient(private val baseUrl: String) {

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
    }

    private fun authHeaders(): HeadersBuilder.() -> Unit = {
        val token = AuthState.getToken()
        if (token != null) {
            append(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private suspend fun handleUnauthorized(response: HttpResponse) {
        if (response.status == HttpStatusCode.Unauthorized) {
            AuthState.logout()
            NavState.navigate(Screen.LOGIN)
        }
    }

    suspend fun requestMagicLink(email: String): MagicLinkResponse {
        val response = client.post("$baseUrl/api/v1/admin/auth/magic-link") {
            contentType(ContentType.Application.Json)
            setBody(MagicLinkRequest(email))
        }
        if (response.status == HttpStatusCode.InternalServerError) {
            throw ServerErrorException()
        }
        if (response.status == HttpStatusCode.NotFound) {
            throw AdminNotFoundException()
        }
        return response.body()
    }

    class ServerErrorException : Exception("Server error")
    class AdminNotFoundException : Exception("Admin not found")

    suspend fun verifyToken(token: String): VerifyTokenResponse {
        val response = client.post("$baseUrl/api/v1/admin/auth/verify") {
            contentType(ContentType.Application.Json)
            setBody(VerifyTokenRequest(token))
        }
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Verification failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun getTeams(search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminTeamResponse> {
        val response = client.get("$baseUrl/api/v1/admin/teams") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getLeagues(search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminLeagueResponse> {
        val response = client.get("$baseUrl/api/v1/admin/leagues") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getFixtures(search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminFixtureResponse> {
        val response = client.get("$baseUrl/api/v1/admin/fixtures") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getPlayers(search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminPlayerResponse> {
        val response = client.get("$baseUrl/api/v1/admin/players") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getSyncRuns(jobName: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminSyncRunResponse> {
        val response = client.get("$baseUrl/api/v1/admin/sync-runs") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!jobName.isNullOrBlank()) parameter("job_name", jobName)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getLegacyLeagues(search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<LegacyLeagueData> {
        val response = client.get("$baseUrl/api/v1/admin/legacy/leagues") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getLegacyTeams(leagueLegacyId: String? = null, search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<LegacyTeamData> {
        val response = client.get("$baseUrl/api/v1/admin/legacy/teams") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!leagueLegacyId.isNullOrBlank()) parameter("league_legacy_id", leagueLegacyId)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getLegacyPlayers(teamLegacyId: String? = null, search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<LegacyPlayerData> {
        val response = client.get("$baseUrl/api/v1/admin/legacy/players") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!teamLegacyId.isNullOrBlank()) parameter("team_legacy_id", teamLegacyId)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun triggerSync(type: String): String {
        val response = client.post("$baseUrl/api/v1/admin/sync/$type") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.Accepted) {
            throw Exception("Sync trigger failed: ${response.status}")
        }
        return response.bodyAsText()
    }

    suspend fun getSyncSchedule(): Map<String, Boolean> {
        val response = client.get("$baseUrl/api/v1/admin/sync/schedule") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun updateSyncSchedule(settings: Map<String, Boolean>): Map<String, Boolean> {
        val response = client.post("$baseUrl/api/v1/admin/sync/schedule") {
            headers(authHeaders())
            contentType(io.ktor.http.ContentType.Application.Json)
            setBody(settings)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getHealth(): twist.mena.sports.shared.dto.common.HealthResponse {
        val response = client.get("$baseUrl/health")
        return response.body()
    }

    suspend fun getTestRuns(offset: Int = 0, limit: Int = 20): OffsetPage<AutomationTestRunResponse> {
        val response = client.get("$baseUrl/api/v1/admin/automation-tests") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getTestRunDetail(runId: String): AutomationTestRunDetailResponse {
        val response = client.get("$baseUrl/api/v1/admin/automation-tests/$runId") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun triggerTestRun(request: TriggerTestRunRequest): TriggerTestRunResponse {
        val response = client.post("$baseUrl/api/v1/admin/automation-tests/trigger") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.Accepted) {
            throw Exception("Failed to trigger test run: ${response.status}")
        }
        return response.body()
    }

    // Notification admin APIs

    suspend fun getNotificationDevices(search: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminDeviceResponse> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/devices") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun searchEntities(query: String, entityType: String? = null, limit: Int = 15): List<AdminEntitySearchResult> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/entities/search") {
            headers(authHeaders())
            parameter("q", query)
            if (!entityType.isNullOrBlank()) parameter("type", entityType)
            parameter("limit", limit)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getNotificationTopics(search: String? = null, type: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminTopicResponse> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/topics") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!search.isNullOrBlank()) parameter("search", search)
            if (!type.isNullOrBlank()) parameter("type", type)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getNotificationHistory(status: String? = null, type: String? = null, offset: Int = 0, limit: Int = 50): OffsetPage<AdminNotificationHistoryResponse> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/history") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
            if (!status.isNullOrBlank()) parameter("status", status)
            if (!type.isNullOrBlank()) parameter("type", type)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getNotificationAnalytics(): AdminNotificationAnalyticsResponse {
        val response = client.get("$baseUrl/api/v1/admin/notifications/analytics") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getTopicSubscribers(entityType: String, entityId: String, offset: Int = 0, limit: Int = 50): OffsetPage<AdminTopicSubscriberResponse> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/topics/$entityType/$entityId/subscribers") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getUserSubscriptions(userId: String): List<AdminUserSubscriptionResponse> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/users/$userId/subscriptions") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun sendNotification(request: AdminSendNotificationRequest): AdminSendNotificationResponse {
        val response = client.post("$baseUrl/api/v1/admin/notifications/send") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK && response.status != HttpStatusCode.Accepted && response.status != HttpStatusCode.BadRequest) {
            throw Exception("Send failed: ${response.status}")
        }
        return response.body()
    }

    suspend fun getScheduledNotifications(offset: Int = 0, limit: Int = 50): OffsetPage<AdminScheduledNotificationResponse> {
        val response = client.get("$baseUrl/api/v1/admin/notifications/scheduled") {
            headers(authHeaders())
            parameter("offset", offset)
            parameter("limit", limit)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun cancelScheduledNotification(id: String) {
        val response = client.patch("$baseUrl/api/v1/admin/notifications/scheduled/$id/cancel") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Cancel failed: ${response.status}")
        }
    }

    suspend fun estimateAudience(request: AdminSendNotificationRequest): AdminAudienceEstimateResponse {
        val response = client.post("$baseUrl/api/v1/admin/notifications/audience/estimate") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun retryNotification(id: String): AdminSendNotificationResponse {
        val response = client.post("$baseUrl/api/v1/admin/notifications/$id/retry") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getOverviewInsights(): AdminOverviewInsightsResponse {
        val response = client.get("$baseUrl/api/v1/admin/overview/insights") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getUserAnalytics(): AdminUserAnalyticsResponse {
        val response = client.get("$baseUrl/api/v1/admin/analytics/users") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        return response.body()
    }

    // Entity Mapping APIs

    suspend fun getEntityMappings(entityType: String? = null): List<EntityMappingResponse> {
        val response = client.get("$baseUrl/api/v1/admin/entity-mappings") {
            headers(authHeaders())
            if (!entityType.isNullOrBlank()) parameter("entity_type", entityType)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun getUnmatchedEntities(entityType: String): List<UnmatchedEntityResponse> {
        val response = client.get("$baseUrl/api/v1/admin/entity-mappings/unmatched") {
            headers(authHeaders())
            parameter("entity_type", entityType)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun createEntityMapping(request: CreateEntityMappingRequest): EntityMappingResponse {
        val response = client.post("$baseUrl/api/v1/admin/entity-mappings") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status == HttpStatusCode.Conflict) {
            throw Exception("Mapping already exists")
        }
        if (response.status != HttpStatusCode.Created) {
            throw Exception("Failed to create mapping: ${response.status}")
        }
        return response.body()
    }

    suspend fun deleteEntityMapping(id: String) {
        val response = client.delete("$baseUrl/api/v1/admin/entity-mappings/$id") {
            headers(authHeaders())
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.NoContent) {
            throw Exception("Failed to delete mapping: ${response.status}")
        }
    }

    suspend fun autoMatchEntities(entityType: String): AutoMatchResponse {
        val response = client.post("$baseUrl/api/v1/admin/entity-mappings/auto-match") {
            headers(authHeaders())
            parameter("entity_type", entityType)
        }
        handleUnauthorized(response)
        return response.body()
    }

    suspend fun updateNameOverride(teamId: String, request: NameOverrideRequest): AdminTeamResponse {
        val response = client.patch("$baseUrl/api/v1/admin/teams/$teamId/name-override") {
            headers(authHeaders())
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        handleUnauthorized(response)
        if (response.status != HttpStatusCode.OK) {
            throw Exception("Update failed: ${response.status}")
        }
        return response.body()
    }
}
